import React, { useEffect, useState } from 'react';
import { Redirect, Switch, Route, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import { ErrorBoundary, RouteWithSubRoutes, NotFound, useFeatures, useSession } from '@cordata/buffet';
import PropTypes from 'prop-types';
import { ListGroup } from 'reactstrap';

import DisputePatientMatchButton from '~/components/Patient/Identity/Dispute/SubmitDispute/Button';
import ServiceReferralEmailButton from '~/components/Patient/Identity/ServiceReferralEmail/Button';
import FormDownloadsButton from '~/components/Patient/Identity/FormDownloads/Button';

import PatientAlerts from '~/components/Patient/Identity/Alerts';

import { authorizedPyInstance as axiosPy } from '~/utils';
import usePatient from '~/utils/patient/hook';

import { PageLayout, SideNav, Navbar } from '~/components';

import PatientBanner from '~/components/Patient/Banner';
import PatientBannerIdentity from '~/components/Patient/BannerIdentity';

import { PatientProvider } from '~/utils/patient/context';
import { MatchStatus } from '~/utils/const/identity';
import log from '~/utils/event/log';
import { hasFullPatientIdentityAccess, isBlockedByMissingLegalStatus } from '~/utils/patient/identity-access';

const LeftHand = ({
  routes,
}) => {
  const location = useLocation();
  const params = useParams();
  const [sideNavItems, setSideNavItems] = useState([]);
  const [, patient] = usePatient();
  const { hasAppIdentity, hasIdentityViewFull } = useFeatures();
  const { current } = useSession();
  const accessibleDivisions = current?.attributes?.accessibleDivisions;

  const CASE_RESTRICTED_ROUTES = routes.filter(r => r.caseRestricted);
  const LEGAL_STATUS_RESTRICTED_ROUTES = routes.filter(r => r.legalStatusRestricted);

  useEffect(() => {
    axiosPy
      .get('/api/data/reference/reference-nav-item', {
        params: {
          ref_data_type: 'patient',
        },
      })
      .then(r => r.data.data)
      .then(r => {
        if (!hasAppIdentity) {
          return r;
        }

        const blockedByCaseRestriction =
          !hasFullPatientIdentityAccess(patient, accessibleDivisions);
        const blockedByMissingLegalStatus = isBlockedByMissingLegalStatus(patient);

        if (!blockedByCaseRestriction && !blockedByMissingLegalStatus) {
          return r;
        }

        return r.filter(rr => (
          !(blockedByCaseRestriction && CASE_RESTRICTED_ROUTES.some(restricted =>
            rr.attributes.route.endsWith(restricted.path)))
          && !(blockedByMissingLegalStatus && LEGAL_STATUS_RESTRICTED_ROUTES.some(restricted =>
            rr.attributes.route.endsWith(restricted.path)))
        ));
      })
      .then(setSideNavItems);
  }, []);

  const displayFormButton = hasFullPatientIdentityAccess(patient, accessibleDivisions);
  const displayHospitalRegistrationSheet = !isBlockedByMissingLegalStatus(patient);

  return (
    <>
      {hasAppIdentity &&
        <PatientBannerIdentity
          {...location.state}
          full={location.pathname === `/patients/${params.patient_id}/primary-contacts`}
          enableDisputeAlertModal
        />
      }

      <SideNav items={sideNavItems} />

      <ListGroup flush>
        {hasAppIdentity && <ServiceReferralEmailButton patientUid={params.patient_id} />}

        {
          hasAppIdentity &&
          hasIdentityViewFull &&
          <FormDownloadsButton
            displayFormButton={displayFormButton}
            displayHospitalRegistrationSheet={displayHospitalRegistrationSheet}
          />
        }

        {
          hasAppIdentity &&
          patient.matchResults?.totalMatches > 0 &&
          patient.matchResults?.canDispute &&
          !patient.matchResults?.hasAllDisputes &&
          Object.keys(patient.matchResults.matchesBySource).filter(s =>
            !patient.matchResults.matchesBySource[s].matches
              .every(m => m.matchStatusId === MatchStatus.Rejected)).length > 0 &&
              <DisputePatientMatchButton />
        }
      </ListGroup>
    </>
  );
};

LeftHand.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const BadgeContainer = () => {
  const [s, patient] = usePatient();

  return s === 'success'
    ? <PatientAlerts {...patient} />
    : null;
};

const Patients = ({ routes, root }) => {
  const location = useLocation();
  const params = useParams();
  const { hasAppIdentity, ...features } = useFeatures();
  const match = useRouteMatch();
  const getQueryParam = (param) => {
    return new URLSearchParams(window.location.search).get(param);
  };
  const referrer = getQueryParam('ref');
  if (hasAppIdentity && referrer === 'external') {
    log.create({ patientUid: params.patient_id, action: 'ViewedPatient' });
  }
  // gross hack to prevent community patient page briefly showing in identity
  const didLoadFeatures = JSON.stringify(features) !== '{}';

  return (didLoadFeatures && (
    <ErrorBoundary>
      <Navbar />

      <PatientProvider {...params}>
        <PageLayout
          Header="div"
          Left={<LeftHand routes={routes} />}
          centerMain={false}
        >
          {hasAppIdentity && (
            <BadgeContainer />
          )}

          {!hasAppIdentity && (
            <PatientBanner
              {...location.state}
              full={location.pathname === `/patients/${params.patient_id}/face-sheet`}
            />
          )}

          <ErrorBoundary>
            <Switch>
              <Route
                exact
                path={match.url}
              >
                <Redirect to={`${match.params.patient_id}/${hasAppIdentity ? 'primary-contacts' : 'face-sheet'}`} />
              </Route>

              {routes?.map(r => (
                <RouteWithSubRoutes
                  key={r.path}
                  {...r}
                  path={root ? r.path : `${match.path}/${r.path}`}
                />
              ))}

              <Route render={props => <NotFound {...props} isRoute />} />
            </Switch>
          </ErrorBoundary>
        </PageLayout>
      </PatientProvider>
    </ErrorBoundary>
  ));
};

Patients.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape()),
  root: PropTypes.bool,
};

Patients.defaultProps = {
  routes: undefined,
  root: false,
};

export default Patients;
